import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import MenuIcon from "@mui/icons-material/Menu";
import Toolbar from "@mui/material/Toolbar";
import Link from "@mui/material/Link";
import { useNavigate, useLocation } from "react-router-dom";
import "./css/navBar.css";
import Button from "../components/button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

interface Props {
  window?: () => Window;
}

export default function DrawerAppBar(props: Props) {
  const drawerWidth = 240;
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const navItems = [
    { name: "Inicio", path: "/" },
    { name: "Nosotros", path: "/nosotros" },
    { name: "Pastores", path: "/pastores" },
    { name: "Soy Nuevo", path: "/soy-nuevo" },
    { name: "Ofrenda", path: "/ofrenda" },
  ];

  const { window } = props;
  const [mobileOpen, setMobileOpen] = React.useState(false);

  // Handle scroll effect
  React.useEffect(() => {
    const handleScroll = () => {
      const isScrolled = globalThis.window.scrollY > 50;
      setScrolled(isScrolled);
    };

    globalThis.window.addEventListener("scroll", handleScroll);
    return () => globalThis.window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  // Menu dropdown handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (path: string) => {
    navigate(path);
    handleMenuClose();
  };

  const container =
    window !== undefined ? () => window().document.body : undefined;

  // NAVBAR DRAWER
  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: "center" }}>
      {/* LOGO  */}
      <img src="./logoIglesia-gold.svg" alt="logo" className="logo" />

      <Divider />

      {/* NAVIGATION */}
      <List>
        {navItems.map((item) => (
          <ListItem key={item.name} disablePadding>
            <ListItemButton
              sx={{ textAlign: "center" }}
              onClick={() => navigate(item.path)}
            >
              <Link
                component="span"
                underline="none"
                color="inherit"
                sx={{
                  fontWeight: "bold",
                  position: "relative",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    width: "0%",
                    height: "2px",
                    bottom: "-2px",
                    left: "0",
                    backgroundColor: "var(--primary-color)",
                    transition: "width 0.3s ease",
                  },
                  "&:hover::after": {
                    width: "100%",
                  },
                }}
              >
                {item.name}
              </Link>
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box className="navBar">
      <CssBaseline />
      <AppBar
        component="nav"
        sx={{
          backgroundColor: scrolled
            ? "rgba(255, 255, 255, 0.1)"
            : "transparent",
          backdropFilter: scrolled ? "blur(10px)" : "none",
          boxShadow: scrolled ? "0 4px 20px rgba(0, 0, 0, 0.1)" : "none",
          transition: "all 0.3s ease",
          borderBottom: scrolled
            ? "1px solid rgba(255, 255, 255, 0.2)"
            : "none",
        }}
      >
        <Toolbar
          className="navbar-container"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            paddingLeft: "var(--container-padding-mobile)",
            paddingRight: "var(--container-padding-mobile)",
            "@media (min-width:768px)": {
              paddingLeft: "var(--container-padding-tablet)",
              paddingRight: "var(--container-padding-tablet)",
            },
            "@media (min-width:1024px)": {
              paddingLeft: "var(--container-padding-desktop)",
              paddingRight: "var(--container-padding-desktop)",
            },
          }}
        >
          {/* LOGO */}
          <img
            src={scrolled ? "/logoIglesia-gold.svg" : "/logoIglesiaWhite.svg"}
            alt="logo"
            className="logo"
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/")}
          />

          {/* MENU ICON (mobile) */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              display: { sm: "none" },
              transition: "color 0.3s ease",
              color: scrolled ? "var(--primary-color)" : "inherit",
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* NAV LINKS (desktop) */}
          <Box
            sx={{
              display: { xs: "none", sm: "flex", alignItems: "center" },
              gap: "var(--space-md)",
            }}
          >
            {navItems.map((item) => {
              if (item.name === "Nosotros") {
                return (
                  <Box key={item.name}>
                    <Link
                      component="button"
                      onClick={handleMenuOpen}
                      underline="none"
                      color={scrolled ? "var(--primary-color)" : "inherit"}
                      sx={{
                        fontWeight: "bold",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        position: "relative",
                        padding: "8px 0",
                        transition: "color 0.3s ease",
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          width: location.pathname === item.path ? "100%" : "0%",
                          height: "2px",
                          bottom: "0",
                          left: "0",
                          backgroundColor: "var(--primary-color)",
                          transition: "width 0.3s ease",
                        },
                        "&:hover::after": {
                          width: "100%",
                        },
                      }}
                    >
                      {item.name}
                    </Link>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleMenuClose}
                      sx={{
                        "& .MuiPaper-root": {
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                          backdropFilter: "blur(10px)",
                          marginTop: "8px",
                        },
                      }}
                    >
                      <MenuItem 
                        onClick={() => handleMenuItemClick("/nosotros")}
                        sx={{
                          fontWeight: "bold",
                          color: "var(--primary-color)",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        Quienes Somos
                      </MenuItem>
                      <MenuItem 
                        onClick={() => handleMenuItemClick("/recursos")}
                        sx={{
                          fontWeight: "bold",
                          color: "var(--primary-color)",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        Recursos
                      </MenuItem>
                    </Menu>
                  </Box>
                );
              }
              return (
                <Link
                  key={item.name}
                  component="button"
                  onClick={() => navigate(item.path)}
                  underline="none"
                  color={scrolled ? "var(--primary-color)" : "inherit"}
                  sx={{
                    fontWeight: "bold",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    position: "relative",
                    padding: "8px 0",
                    transition: "color 0.3s ease",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      width: location.pathname === item.path ? "100%" : "0%",
                      height: "2px",
                      bottom: "0",
                      left: "0",
                      backgroundColor: "var(--primary-color)",
                      transition: "width 0.3s ease",
                    },
                    "&:hover::after": {
                      width: "100%",
                    },
                  }}
                >
                  {item.name}
                </Link>
              );
            })}
            <Link
              component="button"
              onClick={() => navigate("/locacion")}
              sx={{
                textDecoration: "none",
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              <Button
                title={"Visitanos"}
                className={"primary-cta-btn"}
                onClick={() => navigate("/locacion")}
              />
            </Link>
          </Box>
        </Toolbar>
      </AppBar>

      <nav>
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          anchor="right"
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
      </nav>
      <Box component="main" sx={{ p: 3 }}>
        <Toolbar />
      </Box>
    </Box>
  );
}
